import type { FC } from "react";
import { useState, useMemo, useEffect, useRef } from "react";

import styled from "styled-components";

import {
    Combobox,
    PixelPlaceholderLoader,
    SimpleChipItemWrap,
    Tooltip,
    NoBorderButtonWrap,
} from "@similarweb/ui-components";

import type { IChipItem } from "pro-features/components/MultiChipDown/src/MultiChipDownTypes";
import { translate } from "app/filters/i18nFilter";

const MAX_LABEL_LENGTH = 20;

// Chip label: the first selected item's text (truncated) plus a "+N" overflow count.
const buildSelectedText = (selectedItems: string[], items: IChipItem[], selector: keyof IChipItem): string => {
    if (selectedItems.length === 0) {
        return "";
    }
    const firstText = items.find((item) => item[selector] === selectedItems[0])?.text ?? "";
    if (firstText === "") {
        return "";
    }
    const truncated = firstText.length > MAX_LABEL_LENGTH ? `${firstText.slice(0, MAX_LABEL_LENGTH)}...` : firstText;
    const extraCount = selectedItems.length - 1;
    return extraCount > 0 ? `${truncated} +${extraCount}` : truncated;
};

interface IGenericComboboxFilterProps {
    selectedItems: string[];
    items: IChipItem[];
    isLoading?: boolean;
    selector?: string;
    onItemsFilterChange: (items: IChipItem[]) => void;
    onSearch?: (term: string) => void;
    width: string;
    tooltipContent: string;
    buttonText: string;
    placeholder: string;
    shouldUseServerFiltering?: boolean;
}

const SelectedButton = styled(SimpleChipItemWrap)`
    margin: 0;
`;

export const GenericComboboxFilter: FC<IGenericComboboxFilterProps> = ({
    selectedItems,
    items = [],
    selector = "id",
    isLoading = false,
    onItemsFilterChange,
    onSearch,
    width,
    tooltipContent,
    buttonText,
    placeholder,
    shouldUseServerFiltering = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [tempSelectedItems, setTempSelectedItems] = useState<Set<string>>(new Set(selectedItems));
    const [selectionOrder, setSelectionOrder] = useState<string[]>(selectedItems);
    const seenItemsRef = useRef<Map<string, IChipItem>>(new Map());
    // Don't disable if:
    // 1. Dropdown is already open (user is searching)
    // 2. There are selected items (user needs to be able to remove them)
    // 3. We've previously seen items (data exists, just temporarily empty during re-render)
    const isDisabled =
        !isOpen && selectedItems.length === 0 && (isLoading || (items.length === 0 && seenItemsRef.current.size === 0));
    useEffect(() => {
        items.forEach((item) => {
            seenItemsRef.current.set(item.id, item);
        });
    }, [items]);

    useEffect(() => {
        setTempSelectedItems(new Set(selectedItems));
        setSelectionOrder(selectedItems);
    }, [selectedItems]);

    const selectedItemsText = useMemo(() => {
        if (selectedItems.length === 0) {
            return "";
        }

        const combinedItemsMap = new Map<string, IChipItem>();

        items.forEach((item) => combinedItemsMap.set(item.id, item));

        seenItemsRef.current.forEach((item, id) => {
            if (!combinedItemsMap.has(id)) {
                combinedItemsMap.set(id, item);
            }
        });

        const combinedItems = Array.from(combinedItemsMap.values());

        return buildSelectedText(selectedItems, combinedItems, selector as keyof IChipItem);
    }, [selectedItems, items, selector]);

    const mapSelectedIdsToObjects = (selectedIds: string[], availableItems: IChipItem[]) => {
        return selectedIds.map((id) => {
            const item = availableItems.find((i) => i.id === id);
            return { id, text: item?.text ?? id };
        });
    };

    const reset = () => {
        setTempSelectedItems(new Set([]));
        setSelectionOrder([]);
        setSearchValue("");
        onItemsFilterChange([]);
        onSearch?.("");
        setIsOpen(false);
    };

    const handleOpenChange = (open: boolean) => {
        if (isDisabled) {
            return;
        }
        if (!open) {
            const currentSelectedSet = new Set(selectedItems);
            const hasChanges =
                tempSelectedItems.size !== currentSelectedSet.size ||
                !Array.from(tempSelectedItems).every((id) => currentSelectedSet.has(id));

            if (hasChanges) {
                const newSelectedIds = Array.from(tempSelectedItems);
                const selectedItemObjects = mapSelectedIdsToObjects(newSelectedIds, items);
                onItemsFilterChange(selectedItemObjects);
            }

            setSearchValue("");
            onSearch?.("");
        }

        setIsOpen(open);
    };

    const handleCloseItem = () => {
        reset();
        onItemsFilterChange([]);
    };

    const toggleSelection = (itemId: string) => {
        setTempSelectedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });

        setSelectionOrder((prev) => {
            if (prev.includes(itemId)) {
                return prev.filter((id) => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    const removeFromSelected = (itemId: string) => {
        setTempSelectedItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(itemId);
            return newSet;
        });
        setSelectionOrder((prev) => prev.filter((id) => id !== itemId));
    };

    const handleApply = () => {
        const newSelectedIds = Array.from(tempSelectedItems);
        const selectedItemObjects = mapSelectedIdsToObjects(newSelectedIds, items);
        setIsOpen(false);
        setSearchValue("");
        onItemsFilterChange(selectedItemObjects);
    };

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
        onSearch?.(value);
    };

    const filteredSelectedItems = useMemo(() => {
        return selectionOrder
            .filter((id) => tempSelectedItems.has(id))
            .map((id) => {
                const item = items.find((i) => i.id === id);
                return item ?? { id, text: id };
            })
            .filter((item) => {
                return (
                    !searchValue ||
                    shouldUseServerFiltering ||
                    item.text.toLowerCase().includes(searchValue.toLowerCase())
                );
            });
    }, [selectionOrder, tempSelectedItems, items, searchValue, shouldUseServerFiltering]);

    const filteredItems = useMemo(() => {
        if (shouldUseServerFiltering) {
            return items;
        }
        return searchValue
            ? items.filter((item) => item.text.toLowerCase().includes(searchValue.toLowerCase()))
            : items;
    }, [items, searchValue, shouldUseServerFiltering]);

    const hasResults = filteredSelectedItems.length > 0 || filteredItems.length > 0;

    return (
        <Tooltip
            content={tooltipContent}
            enabled={selectedItems.length ? !isOpen : undefined}
        >
            <span>
                <Combobox
                    placement="bottom-start"
                    isOpen={isOpen}
                    onOpenChange={handleOpenChange}
                    width={width}
                >
                    <Combobox.Trigger>
                        <span>
                            {selectedItems.length > 0 && selectedItemsText?.length > 0 ? (
                                <SelectedButton
                                    text={selectedItemsText}
                                    isCompact={false}
                                    onCloseItem={handleCloseItem}
                                />
                            ) : (
                                <NoBorderButtonWrap disabled={isDisabled}>{buttonText}</NoBorderButtonWrap>
                            )}
                        </span>
                    </Combobox.Trigger>

                    <Combobox.SearchInput
                        value={searchValue}
                        onChange={handleSearchChange}
                        placeholder={placeholder}
                    />

                    <Combobox.Content emptyState={isLoading ? undefined : translate("dropdown.no.results")}>
                        {isLoading ? (
                            <Combobox.Option key="__loader">
                                <PixelPlaceholderLoader
                                    width="100%"
                                    height={26}
                                />
                            </Combobox.Option>
                        ) : hasResults ? (
                            <>
                                {tempSelectedItems.size > 0 && filteredSelectedItems.length > 0 ? (
                                    <>
                                        {filteredSelectedItems.map((item) => (
                                            <Combobox.Option
                                                key={`selected-${item.id}`}
                                                value={item}
                                                onClick={() => removeFromSelected(item.id)}
                                            >
                                                <Combobox.OptionLabel>{item.text}</Combobox.OptionLabel>
                                                <Combobox.OptionCheckbox isChecked={tempSelectedItems.has(item.id)} />
                                            </Combobox.Option>
                                        ))}

                                        <Combobox.Separator />
                                    </>
                                ) : null}

                                {filteredItems.map((item) => (
                                    <Combobox.Option
                                        key={item.id}
                                        value={item}
                                        isDisabled={item.disabled}
                                        onClick={() => !item.disabled && toggleSelection(item.id)}
                                    >
                                        <Combobox.OptionLabel>{item.text}</Combobox.OptionLabel>
                                        <Combobox.OptionCheckbox isChecked={tempSelectedItems.has(item.id)} />
                                    </Combobox.Option>
                                ))}
                            </>
                        ) : null}
                    </Combobox.Content>

                    {tempSelectedItems.size > 0 ? (
                        <Combobox.ApplyButton
                            isDisabled={false}
                            onClick={handleApply}
                        >
                            {translate("common.apply.with.count", { count: tempSelectedItems.size })}
                        </Combobox.ApplyButton>
                    ) : null}
                </Combobox>
            </span>
        </Tooltip>
    );
};
